type LaunchEndDateShardedDundasBiDefaultDashboardAppEnvironment = {
    id: string;
    gteEndDate: string;
}[];

interface LaunchInstrumentTypeShardedDundasBiDefaultDashboardAppEnvironment {
    gws: string | LaunchEndDateShardedDundasBiDefaultDashboardAppEnvironment;
    gba: string | LaunchEndDateShardedDundasBiDefaultDashboardAppEnvironment;
    // Add other dashboard IDs for other instruments types here
};

/**
 * The interface for the app environment.
 */
export interface AppEnvironment {
    /** The map of supported languages for this app and their info. */
    supportedLanguages: {
        [languageTag: string]: {
            /** 
             * A headsup statement to notify users that we are soon undergoing 
             * maintenance.
             */
            underMaintenanceHeadsupStatement: string;
            privacyPolicy: string;
            termsOfUse: string;
        };
    };
    /** 
     * The URL prefix that should be preserved when generating and recognizing 
     * URLs. 
     */
    baseHref: string;
    /** The current build timesamp */
    buildTimestamp: string;
    /** A flag denoting if the this is a production environment. */
    production: boolean;
    /** Details partaining to backend API and microservices. */
    api: {
        /** 
         * The base URL for the API backend if all the services have a common 
         * base URL. 
         */
        baseUrl: string | undefined;
        /**
         * 
         */
        xApiKey: string
        /** 
         * URLs to API backend services that are required through out the app. 
         * Can be relative URLs if no base URL is provided. Also, can house 
         * granular endpoints or even multiple maintained versions. 
         */
        services: {
            authentication: string;
            glossary:       string
        };
    };
    /**
     * Keys used to maintain the uniqueness of stored values in the browser when 
     * having the app served to the browser from the different environments.
     */
    storageKeys: {
        auth: {
            /** Key to store access token. */
            accessToken: string;
            /** Key to store refresh token. */
            refreshToken: string;
            /** Key to store refresh token expiry. */
            refreshTokenExpiry: string;
            /** Key to store selected authorized organization. */
            currentOrganization: string;
            /** Key to store all authorized organizations. */
            authorizedOrganizations: string;
        };
        /** Key to store the remember me email for fast login. */
        rememberMe: string;
    };
    /** Under maintenance mode configurations. */
    underMaintenance: {
        /** 
         * A flag denoting if the app is under maintenance. Takes more 
         * precedence over setting an activation date. 
         */
        activated: boolean | undefined;
        /** Set an datetime of when under maintenace mode is activated. */
        activationDate: string | undefined; // Format YYYY-MM-DDTHH:mm:ss.sssZ
        /** For enabling the headsup statement. */
        headsupEnabled: boolean;
    };
};

/**
 * Overrides the passed app environment with corresponding variables in the 
 * user environment.
 * 
 * @param environment The app environment variables
 * @returns The mutated app environment variables
 */
export function overrideWithUserEnvironment(
    appEnv: AppEnvironment, 
    userEnv: Record<string, string | number | boolean> | undefined,
): AppEnvironment {
    /**
     * Recursive helper method.
     * 
     * @param property A property that is a non-primitive type object 
     * https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-2.html#object-type
     * @param currentPath The current path of the property in the app environment
     */
    function traverse(
        property: object, 
        currentPath: string[], 
        userEnv: Record<string, string | number | boolean>,
    ) {
        for (const key in property) {
            if (property.hasOwnProperty(key)) {
                const value = property[key as keyof typeof property];
                const path = [...currentPath, key];
                if (value !== null && typeof value === 'object') {
                    traverse(value, path, userEnv);
                } else {
                    const userVariableValue = userEnv[`NG_APP_${path.join('_').toUpperCase()}`];
                    if (userVariableValue !== undefined) {
                        // Override the property in the config object
                        property[key as keyof typeof property] = userVariableValue as never;
                        // console.log(`Overriding the environment variable ${path.join('.')} with = ${userVariableValue}`);
                    }
                }
            }
        }
    }

    if (userEnv != undefined) traverse(appEnv, [], userEnv);
    return appEnv;
}
